/**
 * KnowledgeGraphCanvas — interactive force-directed visualization of
 * the knowledge graph, drawn directly to HTML canvas (no d3 dep).
 *
 * Layout: classic spring-electrical (Fruchterman–Reingold-ish):
 *   - Coulomb-style repulsion between every node pair, O(N²) per tick
 *     (fine for the ~100-node sandbox scale; if it ever needs to scale
 *     further the next step is Barnes-Hut, but that's a 10× LOC bump).
 *   - Hooke spring along each edge, pulling endpoints toward `restLength`.
 *   - Soft centering force so the graph stays in viewport.
 *   - Velocity damping every tick — the sim "cools" and stops when
 *     kinetic energy drops below a threshold. Auto-resumes whenever
 *     the upstream snapshot version increments.
 *
 * Interactions:
 *   - Drag empty space     → pan
 *   - Mouse wheel          → zoom (anchored on cursor)
 *   - Drag a node          → pins it during drag, releases on mouseup
 *   - Click a node         → select (highlights node + its 1-hop neighbours)
 *   - Hover a node         → tooltip with id + degree
 *   - Click empty space    → deselect
 *
 * Visual encoding:
 *   - Node color/shape by kind (spec=blue square, file=teal circle,
 *     test=amber circle, symbol=purple small circle).
 *   - Node radius scales with degree.
 *   - Edge color by kind; dashed when status='dangling'.
 *   - Selected node + neighbours: full opacity; everyone else: dimmed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KGNode, KGSnapshot } from '@gotti/sandbox-ai-v2';
import { KG_EDGE_LABEL } from '@gotti/sandbox-ai-v2';

interface SimNode {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** When true, position is held fixed (user is dragging it). */
  pinned: boolean;
  /** Cached degree (in + out), refreshed on snapshot change. */
  degree: number;
}

const NODE_FILL: Record<string, string> = {
  spec:   '#88b9f0',
  file:   '#4ec9b0',
  test:   '#d4b256',
  symbol: '#c884dc',
  runbook:'#64b4f0',
  asset:  '#e0a458',
  api:    '#6fcf97',
};

const NODE_STROKE: Record<string, string> = {
  spec:   '#3a6fb7',
  file:   '#2d7d6e',
  test:   '#8d7a32',
  symbol: '#7e4f8c',
  runbook:'#2f6fa8',
  asset:  '#a06d2c',
  api:    '#3f8f5f',
};

const EDGE_COLOR: Record<string, string> = {
  'file-imports-file':     'rgba(78, 201, 176, 0.45)',
  'file-exports-symbol':   'rgba(200, 132, 220, 0.45)',
  'spec-describes-symbol': 'rgba(115, 165, 235, 0.55)',
  'spec-references-spec':  'rgba(115, 165, 235, 0.75)',
  'test-covers-file':      'rgba(220, 180, 90, 0.55)',
  'file-defines-api':      'rgba(111, 207, 151, 0.65)',
  'file-uses-api':         'rgba(111, 207, 151, 0.40)',
  'file-uses-asset':       'rgba(224, 164, 88, 0.50)',
  'runbook-addresses-symbol': 'rgba(100, 180, 240, 0.55)',
  'runbook-references-spec':  'rgba(100, 180, 240, 0.75)',
};

const NODE_BASE_RADIUS = 5;
const NODE_DEGREE_RADIUS_SCALE = 0.9;
const NODE_MAX_RADIUS = 16;
const EDGE_REST_LENGTH = 70;
const SPRING_K = 0.025;
const REPULSION_K = 800;
const CENTERING_K = 0.005;
const DAMPING = 0.82;
const KE_STOP_THRESHOLD = 0.05;
const MAX_TICKS_PER_FRAME = 2;

interface KnowledgeGraphCanvasProps {
  snapshot: KGSnapshot;
  /** When set, this node gets the "selected" highlight. */
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** Show only project-internal nodes (hide `external:*`). */
  hideExternal?: boolean;
}

export function KnowledgeGraphCanvas({
  snapshot,
  selectedId,
  onSelect,
  hideExternal = true,
}: KnowledgeGraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const simNodesRef = useRef<Map<string, SimNode>>(new Map());
  const transformRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const animatingRef = useRef(false);
  const lastSnapVersionRef = useRef(-1);
  const interactionRef = useRef<{
    mode: 'idle' | 'pan' | 'drag';
    draggingNodeId: string | null;
    lastX: number;
    lastY: number;
  }>({ mode: 'idle', draggingNodeId: null, lastX: 0, lastY: 0 });

  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [size, setSize] = useState({ w: 600, h: 400 });

  // ── Filter snapshot + build adjacency ────────────────────────────────
  const { nodes, edges, adjacency } = useMemo(() => {
    const visibleNodes = snapshot.nodes.filter(
      n => !hideExternal || !n.id.startsWith('external:'),
    );
    const visibleSet = new Set(visibleNodes.map(n => n.id));
    const visibleEdges = snapshot.edges.filter(
      e => visibleSet.has(e.source) && visibleSet.has(e.target),
    );
    const adj = new Map<string, Set<string>>();
    for (const e of visibleEdges) {
      if (!adj.has(e.source)) adj.set(e.source, new Set());
      if (!adj.has(e.target)) adj.set(e.target, new Set());
      adj.get(e.source)!.add(e.target);
      adj.get(e.target)!.add(e.source);
    }
    return { nodes: visibleNodes, edges: visibleEdges, adjacency: adj };
  }, [snapshot, hideExternal]);

  // ── Resize observer ──────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ w: Math.max(200, Math.floor(width)), h: Math.max(200, Math.floor(height)) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Sync sim nodes with snapshot (add new, drop stale, keep positions) ─
  useEffect(() => {
    const sim = simNodesRef.current;
    const wantIds = new Set(nodes.map(n => n.id));

    // Drop nodes no longer in snapshot.
    for (const id of [...sim.keys()]) {
      if (!wantIds.has(id)) sim.delete(id);
    }

    // Add newcomers. Place near the centre with a small random jitter
    // so the layout has room to spread them.
    const centerX = size.w / 2;
    const centerY = size.h / 2;
    for (const n of nodes) {
      if (sim.has(n.id)) {
        // Refresh cached degree even if position is preserved.
        const sn = sim.get(n.id)!;
        sn.degree = adjacency.get(n.id)?.size ?? 0;
        continue;
      }
      sim.set(n.id, {
        id: n.id,
        x: centerX + (Math.random() - 0.5) * 80,
        y: centerY + (Math.random() - 0.5) * 80,
        vx: 0,
        vy: 0,
        pinned: false,
        degree: adjacency.get(n.id)?.size ?? 0,
      });
    }

    // If the snapshot version changed, wake the sim back up.
    if (snapshot.version !== lastSnapVersionRef.current) {
      lastSnapVersionRef.current = snapshot.version;
      kickSim();
    }
  }, [nodes, adjacency, snapshot.version, size.w, size.h]);

  // ── Simulation step ──────────────────────────────────────────────────
  const step = useCallback((): number => {
    const sim = simNodesRef.current;
    const nodeArr = [...sim.values()];
    const n = nodeArr.length;
    if (n === 0) return 0;

    // Reset forces by writing back into vx/vy after applying.
    // O(N²) repulsion.
    const fx = new Float32Array(n);
    const fy = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const a = nodeArr[i];
      for (let j = i + 1; j < n; j++) {
        const b = nodeArr[j];
        let dx = a.x - b.x;
        let dy = a.y - b.y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 0.01) {
          // Coincident — break the symmetry.
          dx = (Math.random() - 0.5) * 0.5;
          dy = (Math.random() - 0.5) * 0.5;
          d2 = dx * dx + dy * dy;
        }
        const inv = REPULSION_K / d2;
        const dist = Math.sqrt(d2);
        const ux = dx / dist;
        const uy = dy / dist;
        fx[i] += ux * inv;
        fy[i] += uy * inv;
        fx[j] -= ux * inv;
        fy[j] -= uy * inv;
      }
    }

    // Spring attraction along edges.
    const idxOf = new Map<string, number>();
    for (let i = 0; i < n; i++) idxOf.set(nodeArr[i].id, i);
    for (const e of edges) {
      const i = idxOf.get(e.source);
      const j = idxOf.get(e.target);
      if (i === undefined || j === undefined) continue;
      const a = nodeArr[i];
      const b = nodeArr[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const ext = dist - EDGE_REST_LENGTH;
      const f = SPRING_K * ext;
      const ux = dx / dist;
      const uy = dy / dist;
      fx[i] += ux * f;
      fy[i] += uy * f;
      fx[j] -= ux * f;
      fy[j] -= uy * f;
    }

    // Soft centering — pull everything back toward (cx, cy).
    const cx = size.w / 2;
    const cy = size.h / 2;
    for (let i = 0; i < n; i++) {
      const a = nodeArr[i];
      fx[i] += (cx - a.x) * CENTERING_K;
      fy[i] += (cy - a.y) * CENTERING_K;
    }

    // Integrate with damping.
    let kineticEnergy = 0;
    for (let i = 0; i < n; i++) {
      const a = nodeArr[i];
      if (a.pinned) {
        a.vx = 0;
        a.vy = 0;
        continue;
      }
      a.vx = (a.vx + fx[i]) * DAMPING;
      a.vy = (a.vy + fy[i]) * DAMPING;
      a.x += a.vx;
      a.y += a.vy;
      kineticEnergy += a.vx * a.vx + a.vy * a.vy;
    }
    return kineticEnergy;
  }, [edges, size.w, size.h]);

  // ── Render frame to canvas ───────────────────────────────────────────
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== size.w * dpr || canvas.height !== size.h * dpr) {
      canvas.width = size.w * dpr;
      canvas.height = size.h * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);

    const { scale, tx, ty } = transformRef.current;
    ctx.save();
    ctx.translate(tx, ty);
    ctx.scale(scale, scale);

    const sim = simNodesRef.current;
    const nodeKindById = new Map(nodes.map(n => [n.id, n.kind]));
    const nodeLabelById = new Map(nodes.map(n => [n.id, n.label]));

    const highlightId = selectedId ?? hoveredId;
    const neighborSet = highlightId ? (adjacency.get(highlightId) ?? new Set()) : null;
    const isDimmed = (id: string) => {
      if (!highlightId) return false;
      if (id === highlightId) return false;
      return !(neighborSet?.has(id));
    };

    // Edges first.
    ctx.lineWidth = 1;
    for (const e of edges) {
      const a = sim.get(e.source);
      const b = sim.get(e.target);
      if (!a || !b) continue;
      const dim = isDimmed(e.source) && isDimmed(e.target);
      ctx.strokeStyle = EDGE_COLOR[e.kind] ?? 'rgba(255,255,255,0.2)';
      ctx.globalAlpha = dim ? 0.08 : 1;
      ctx.beginPath();
      if (e.status === 'dangling') {
        ctx.setLineDash([3, 3]);
      } else {
        ctx.setLineDash([]);
      }
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    // Nodes on top.
    for (const sn of sim.values()) {
      const kind = nodeKindById.get(sn.id);
      if (!kind) continue;
      const r = Math.min(
        NODE_MAX_RADIUS,
        NODE_BASE_RADIUS + sn.degree * NODE_DEGREE_RADIUS_SCALE,
      );
      const fill = NODE_FILL[kind] ?? '#bbb';
      const stroke = NODE_STROKE[kind] ?? '#444';
      const dim = isDimmed(sn.id);
      ctx.globalAlpha = dim ? 0.18 : 1;

      ctx.fillStyle = fill;
      ctx.strokeStyle = sn.id === highlightId ? '#fff' : stroke;
      ctx.lineWidth = sn.id === highlightId ? 2.5 : 1.2;

      ctx.beginPath();
      if (kind === 'spec') {
        // Rounded square for specs — easier to spot.
        ctx.rect(sn.x - r, sn.y - r, r * 2, r * 2);
      } else {
        ctx.arc(sn.x, sn.y, r, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Labels — only for highlight + immediate neighbours, otherwise too
    // noisy. Always show for the hovered/selected node.
    ctx.font = '11px -apple-system, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const showLabel = (id: string) =>
      id === highlightId || (highlightId && neighborSet?.has(id));
    for (const sn of sim.values()) {
      if (!showLabel(sn.id)) continue;
      const label = nodeLabelById.get(sn.id) ?? sn.id;
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(sn.x + 10, sn.y - 8, ctx.measureText(label).width + 8, 16);
      ctx.fillStyle = '#fff';
      ctx.fillText(label, sn.x + 14, sn.y);
    }

    ctx.restore();
  }, [edges, nodes, adjacency, selectedId, hoveredId, size.w, size.h]);

  // ── Animation loop ───────────────────────────────────────────────────
  const kickSim = useCallback(() => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    const loop = () => {
      let ke = 0;
      for (let t = 0; t < MAX_TICKS_PER_FRAME; t++) ke = step();
      render();
      const someoneDragging = interactionRef.current.mode === 'drag';
      if (ke > KE_STOP_THRESHOLD || someoneDragging) {
        requestAnimationFrame(loop);
      } else {
        animatingRef.current = false;
      }
    };
    requestAnimationFrame(loop);
  }, [render, step]);

  // Re-render on every snapshot/highlight change even if sim is paused.
  useEffect(() => {
    render();
  }, [render]);

  // ── Mouse / wheel handlers ───────────────────────────────────────────
  // World ↔ screen helpers.
  const screenToWorld = (sx: number, sy: number): [number, number] => {
    const { scale, tx, ty } = transformRef.current;
    return [(sx - tx) / scale, (sy - ty) / scale];
  };
  const hitTest = (wx: number, wy: number): string | null => {
    let best: { id: string; d2: number } | null = null;
    for (const sn of simNodesRef.current.values()) {
      const dx = sn.x - wx;
      const dy = sn.y - wy;
      const d2 = dx * dx + dy * dy;
      const r = Math.min(
        NODE_MAX_RADIUS,
        NODE_BASE_RADIUS + sn.degree * NODE_DEGREE_RADIUS_SCALE,
      );
      if (d2 <= (r + 3) * (r + 3) && (!best || d2 < best.d2)) {
        best = { id: sn.id, d2 };
      }
    }
    return best?.id ?? null;
  };

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const [wx, wy] = screenToWorld(sx, sy);
    const hit = hitTest(wx, wy);
    if (hit) {
      const sn = simNodesRef.current.get(hit);
      if (sn) sn.pinned = true;
      interactionRef.current = { mode: 'drag', draggingNodeId: hit, lastX: sx, lastY: sy };
      kickSim();
    } else {
      interactionRef.current = { mode: 'pan', draggingNodeId: null, lastX: sx, lastY: sy };
    }
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const i = interactionRef.current;

    if (i.mode === 'pan') {
      transformRef.current.tx += sx - i.lastX;
      transformRef.current.ty += sy - i.lastY;
      i.lastX = sx;
      i.lastY = sy;
      render();
      return;
    }
    if (i.mode === 'drag' && i.draggingNodeId) {
      const sn = simNodesRef.current.get(i.draggingNodeId);
      if (sn) {
        const [wx, wy] = screenToWorld(sx, sy);
        sn.x = wx;
        sn.y = wy;
        sn.vx = 0;
        sn.vy = 0;
      }
      i.lastX = sx;
      i.lastY = sy;
      kickSim();
      return;
    }

    // idle — hover tracking
    const [wx, wy] = screenToWorld(sx, sy);
    const hit = hitTest(wx, wy);
    if (hit !== hoveredId) setHoveredId(hit);
  };

  const onMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const i = interactionRef.current;
    if (i.mode === 'drag' && i.draggingNodeId) {
      const sn = simNodesRef.current.get(i.draggingNodeId);
      if (sn) sn.pinned = false;
      // Treat tap-style drags (no movement) as click → select.
      const rect = e.currentTarget.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      if (Math.abs(sx - i.lastX) < 2 && Math.abs(sy - i.lastY) < 2) {
        onSelect?.(i.draggingNodeId);
      }
    } else if (i.mode === 'pan') {
      // Click on empty space → deselect.
      const rect = e.currentTarget.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      if (Math.abs(sx - i.lastX) < 2 && Math.abs(sy - i.lastY) < 2) {
        onSelect?.(null);
      }
    }
    interactionRef.current = { mode: 'idle', draggingNodeId: null, lastX: 0, lastY: 0 };
  };

  const onMouseLeave = () => {
    interactionRef.current = { mode: 'idle', draggingNodeId: null, lastX: 0, lastY: 0 };
    setHoveredId(null);
  };

  const onWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const t = transformRef.current;
    const oldScale = t.scale;
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.2, Math.min(5, oldScale * factor));
    // Keep the cursor's world point stable across zoom.
    const wx = (sx - t.tx) / oldScale;
    const wy = (sy - t.ty) / oldScale;
    t.scale = newScale;
    t.tx = sx - wx * newScale;
    t.ty = sy - wy * newScale;
    render();
  };

  const hoveredNode = hoveredId ? nodes.find(n => n.id === hoveredId) : null;
  const selectedNode = selectedId ? nodes.find(n => n.id === selectedId) : null;
  const tooltipNode = hoveredNode ?? selectedNode;

  return (
    <div className="kg-canvas-host" ref={containerRef} data-testid="kg-canvas-host">
      <canvas
        ref={canvasRef}
        className="kg-canvas"
        width={size.w}
        height={size.h}
        style={{ width: size.w, height: size.h }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onWheel={onWheel}
        data-testid="kg-canvas"
      />
      <KGLegend />
      {tooltipNode && (
        <KGTooltip node={tooltipNode} degree={adjacency.get(tooltipNode.id)?.size ?? 0} />
      )}
    </div>
  );
}

function KGLegend() {
  return (
    <div className="kg-legend">
      <span className="kg-legend-item"><span className="kg-legend-swatch kg-pill-spec" />spec</span>
      <span className="kg-legend-item"><span className="kg-legend-swatch kg-pill-file" />file</span>
      <span className="kg-legend-item"><span className="kg-legend-swatch kg-pill-test" />test</span>
      <span className="kg-legend-item"><span className="kg-legend-swatch kg-pill-symbol" />symbol</span>
      <span className="kg-legend-item"><span className="kg-legend-swatch kg-pill-runbook" />runbook</span>
      <span className="kg-legend-item"><span className="kg-legend-swatch kg-pill-asset" />asset</span>
      <span className="kg-legend-item"><span className="kg-legend-swatch kg-pill-api" />api</span>
      <span className="kg-legend-sep">|</span>
      <span className="kg-legend-item-faint">drag node · pan empty · wheel zoom</span>
    </div>
  );
}

function KGTooltip({ node, degree }: { node: KGNode; degree: number }) {
  return (
    <div className="kg-canvas-tooltip">
      <div className="kg-canvas-tooltip-header">
        <span className={`kg-pill kg-pill-${node.kind}`}>{node.kind}</span>
        <span className="kg-canvas-tooltip-label">{node.label}</span>
      </div>
      <div className="kg-canvas-tooltip-meta">
        <code>{node.id}</code>
        <span>{degree} edge{degree === 1 ? '' : 's'}</span>
      </div>
    </div>
  );
}

// Keep the import-only edge label namespace warm for callers that want
// to read it; this is the single export point of the canvas module.
export { KG_EDGE_LABEL as _KG_EDGE_LABEL_UNUSED };
